import { useTranslation } from "next-i18next";
import { ContactFormTextarea } from "@components/sections/contact-form/form-fields/contact-form-textarea";
import { FC, useState } from "react";
import { CheckBoxPrivacy } from "@components/sections/contact-form/form-fields/checkbox-privacy";
import { ContactFormInput } from "@components/sections/contact-form/form-fields/contact-form-input";
import { ContactFormServiceSelect } from "@components/sections/contact-form/form-fields/contact-form-service-select";
import { CheckboxNewsletter } from "@components/sections/contact-form/form-fields/checkbox-newsletter";
import { FormProcessing } from "@components/helpers/enums";
import { FetchPostCall } from "@components/helpers/fetch-call";
import { ContactFormInputRequired } from "@components/sections/contact-form/form-fields/contact-form-input-required";
import { i18translateType } from "../../../@types/types-body";

const ContactForm: FC = () => {
  const namespace = "common";
  const { t }: i18translateType = useTranslation(namespace);
  const defaultModel = {
    first_name: {
      model: "",
      isValid: true,
    },
    last_name: {
      model: "",
      isValid: true,
    },
    email: {
      model: "",
      isValid: true,
    },
    phone: {
      model: "",
      isValid: true,
    },
    company: {
      model: "",
      isValid: true,
    },
    website: {
      model: "",
      isValid: true,
    },
    message: {
      model: "",
      isValid: true,
    },
    acceptPrivacySettings: {
      model: false,
      isValid: true,
    },
    joinNewsletter: {
      model: false,
      isValid: true,
    },
  };
  const [contactForm, setContactForm] = useState<any>(defaultModel);
  const [joinNewsletter, setJoinNewsletter] = useState<boolean>(false);
  const [acceptPrivacySettings, setAcceptPrivacySettings] = useState(false);
  const [isValidAcceptPrivacySettings, setIsValidAcceptPrivacySettings] =
    useState<boolean>(true);
  const [formState, setFormState] = useState("");

  function ValidateForm() {
    const modelFirstName = contactForm.first_name.model.length >= 2;
    const modelLastName = contactForm.last_name.model.length >= 2;
    const regexEmail = new RegExp("^[\\w-\\.]+@([\\w-]+\\.)+[\\w-]{2,4}$");
    const modelEmail =
      contactForm.email.model.length >= 5 &&
      regexEmail.test(contactForm.email.model);
    const modelPhone = contactForm.phone.model.length >= 2;
    const modelMessage = contactForm.message.model.length >= 30;
    setContactForm((prevState: any) => ({
      first_name: {
        model: prevState.first_name.model,
        isValid: modelFirstName,
      },
      last_name: {
        model: prevState.last_name.model,
        isValid: modelLastName,
      },
      email: {
        model: prevState.email.model,
        isValid: modelEmail,
      },
      phone: {
        model: prevState.phone.model,
        isValid: modelPhone,
      },
      company: {
        model: prevState.company.model,
        isValid: prevState.company.model,
      },
      website: {
        model: prevState.website.model,
        isValid: prevState.website.model,
      },
      message: {
        model: prevState.message.model,
        isValid: modelMessage,
      },
      acceptPrivacySettings: {
        model: acceptPrivacySettings,
        isValid: isValidAcceptPrivacySettings,
      },
      joinNewsletter: {
        model: joinNewsletter,
        isValid: true,
      },
    }));
  }

  function ValidatePrivacySettings() {
    setIsValidAcceptPrivacySettings(acceptPrivacySettings);
  }

  async function handleSubmit(event: any) {
    event.preventDefault();
    ValidateForm();
    ValidatePrivacySettings();
    if (
      contactForm.first_name.isValid &&
      contactForm.last_name.isValid &&
      contactForm.message.isValid &&
      contactForm.email.isValid &&
      contactForm.phone.isValid &&
      acceptPrivacySettings
    ) {
      setFormState(FormProcessing.Processing);
      try {
        const result = await FetchPostCall("/api/contact", contactForm);
        if (contactForm.joinNewsletter.model) {
          await FetchPostCall("/api/newsletter", {
            email: contactForm.email.model,
          });
        }
        if (result.status >= 200 && result.status < 300) {
          setFormState(FormProcessing.Success);
          setContactForm(defaultModel);
        } else {
          setFormState(FormProcessing.Error);
          setTimeout(() => {
            setFormState(FormProcessing.Empty);
          }, 4000);
        }
      } catch (e) {
        setFormState(FormProcessing.Error);
        setTimeout(() => {
          setFormState(FormProcessing.Empty);
        }, 4000);
      }
    } else {
      setFormState(FormProcessing.Invalid);
      setTimeout(() => {
        setFormState(FormProcessing.Empty);
      }, 4000);
    }
  }

  return (
    <section className="contact-area">
      <form>
        <div className="container mx-auto">
          <div className="grid grid-cols-12 gap-4 px-2">
            <div className="col-span-12 md:col-start-3 md:col-end-11">
              <h1 className="text-center text-xl lg:text-3xl">
                {t("contact_form.contact_us")}
              </h1>
            </div>
            <div className="col-span-12 md:col-start-3 md:col-end-11">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-6">
                  <ContactFormInputRequired
                    id={1}
                    namespace={namespace}
                    placeholder={"contact_form.first_name"}
                    type={"text"}
                    model={contactForm.first_name}
                    name={"first_name"}
                    errorMessage={"contact_form.first_name_invalid"}
                    onChange={setContactForm}
                  />
                </div>
                <div className="col-span-12 md:col-span-6">
                  <ContactFormInputRequired
                    id={2}
                    namespace={namespace}
                    placeholder={"contact_form.last_name"}
                    type={"text"}
                    model={contactForm.last_name}
                    name={"last_name"}
                    errorMessage={"contact_form.last_name_invalid"}
                    onChange={setContactForm}
                  />
                </div>
                <div className="col-span-12 md:col-span-6">
                  <ContactFormInputRequired
                    id={3}
                    namespace={namespace}
                    placeholder={"contact_form.email"}
                    type={"email"}
                    model={contactForm.email}
                    name={"email"}
                    errorMessage={"contact_form.email_invalid"}
                    onChange={setContactForm}
                  />
                </div>
                <div className="col-span-12 md:col-span-6">
                  <ContactFormInputRequired
                    id={4}
                    namespace={namespace}
                    placeholder={"contact_form.phone"}
                    type={"phone"}
                    model={contactForm.phone}
                    name={"phone"}
                    errorMessage={"contact_form.phone_invalid"}
                    onChange={setContactForm}
                  />
                </div>
                <div className="col-span-12 md:col-span-6">
                  <ContactFormInput
                    id={5}
                    namespace={namespace}
                    placeholder={"contact_form.company"}
                    type={"text"}
                    model={contactForm.company}
                    name={"company"}
                    onChange={setContactForm}
                  />
                </div>
                <div className="col-span-12 md:col-span-6">
                  <ContactFormInput
                    id={6}
                    namespace={namespace}
                    placeholder={"contact_form.website"}
                    type={"text"}
                    model={contactForm.website}
                    name={"website"}
                    onChange={setContactForm}
                  />
                </div>
              </div>
              <div className="col-span-12 my-4">
                <ContactFormTextarea
                  id={8}
                  namespace={namespace}
                  placeholder={"contact_form.message"}
                  model={contactForm.message}
                  name={"message"}
                  onChange={setContactForm}
                  errorMessage={"contact_form.message_invalid"}
                />
              </div>
              <div className="col-span-12 mt-8">
                <span className="text-center text-base">
                  {t("contact_form.join_our_newsletter")}
                </span>
                <CheckboxNewsletter
                  id={9}
                  namespace={namespace}
                  placeholder={"contact_form.newsletter"}
                  model={joinNewsletter}
                  onChange={setJoinNewsletter}
                />
                <span className="text-center text-sm">
                  {t("contact_form.join_our_newsletter_info")}
                </span>
              </div>

              <div className="col-span-12 my-8">
                <div
                  className={
                    isValidAcceptPrivacySettings
                      ? "hidden"
                      : "w-full text-center mx-auto text-red-800"
                  }
                >
                  {t("accept_privacy_invalid")}
                </div>
                <CheckBoxPrivacy
                  id={10}
                  model={acceptPrivacySettings}
                  onChange={setAcceptPrivacySettings}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col w-full mt-8 align-text-bottom">
          <span
            className={
              formState === FormProcessing.Invalid
                ? "text-red-800 font-bold font-xl text-center mb-2"
                : "hidden"
            }
          >
            {t("contact_form.invalid")}
          </span>
          <span
            className={
              formState === FormProcessing.Processing
                ? "text-red-800 font-bold font-xl text-center mb-2"
                : "hidden"
            }
          >
            {t("contact_form.invalid")}
          </span>

          <button
            type="submit"
            onClick={async (event) => await handleSubmit(event)}
            className={`${
              formState === FormProcessing.Processing
                ? "bg-gray-800"
                : "bg-crowlyn-light"
            }  mx-auto py-2 px-6  rounded-lg text-white font-bold flex`}
            disabled={
              formState === FormProcessing.Processing ||
              formState === FormProcessing.Success ||
              formState === FormProcessing.Error
            }
          >
            <svg
              className={
                formState === FormProcessing.Processing
                  ? "animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  : "hidden"
              }
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="5"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {t("contact_form.submit")}
          </button>
          <span
            className={
              formState === FormProcessing.Success
                ? "text-crowlyn-light font-bold font-2xl text-center my-2"
                : "hidden"
            }
          >
            {t(`contact_form.${FormProcessing.Success}`)}
          </span>
          <span
            className={
              formState === FormProcessing.Error
                ? "text-red-800 font-bold font-2xl text-center my-2"
                : "hidden"
            }
          >
            {t(`contact_form.${FormProcessing.Error}`)}
          </span>
        </div>
      </form>
    </section>
  );
};

export default ContactForm;
